import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { maskUserName, maskEmail } from '../utils/piiMask';

// ── Shared helpers ─────────────────────────────────────────────────────────────
function useAsync(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fn();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line

  useEffect(() => { run(); }, [run]);
  return { data, loading, error, reload: run };
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--hairline)',
      borderRadius: 'var(--r)', padding: '18px 20px',
    }}>
      <div style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--serif)', color: accent || 'var(--forest)' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--hairline)', marginBottom: 28 }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            padding: '10px 18px', border: 'none', background: 'none',
            fontFamily: 'var(--sans)', fontSize: 13, fontWeight: active === t.id ? 600 : 400,
            color: active === t.id ? 'var(--forest)' : 'var(--ink-3)',
            borderBottom: active === t.id ? '2px solid var(--forest)' : '2px solid transparent',
            marginBottom: -1, cursor: 'pointer', transition: 'all .15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Badge({ label, color }) {
  const colors = {
    green: { bg: '#e8f5e9', text: '#2e7d32' },
    red: { bg: '#fdecea', text: '#b71c1c' },
    amber: { bg: '#fff8e1', text: '#f57f17' },
    blue: { bg: '#e3f2fd', text: '#1565c0' },
    purple: { bg: '#f3e5f5', text: '#6a1b9a' },
    grey: { bg: 'var(--paper-2)', text: 'var(--ink-3)' },
  };
  const c = colors[color] || colors.grey;
  return (
    <span style={{
      background: c.bg, color: c.text,
      fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
      padding: '2px 7px', borderRadius: 20, textTransform: 'uppercase',
    }}>{label}</span>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab() {
  const { data: stats, loading, error, reload } = useAsync(() => adminAPI.getStats());

  if (loading) return <div style={{ color: 'var(--ink-4)', padding: 40, textAlign: 'center' }}>Loading…</div>;
  if (error) return <ErrorBanner msg={error} onRetry={reload} />;

  const roleColors = { owner: 'green', agent: 'blue', buyer: 'amber', pro: 'purple', admin: 'red' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
        <StatCard label="Total Users" value={stats.total_users} sub={`${stats.active_users} active`} />
        <StatCard label="New (30d)" value={stats.new_users_last_30d} accent="var(--moss)" />
        <StatCard label="Properties" value={stats.total_properties} />
        <StatCard label="Bookings" value={stats.total_bookings} sub={`${stats.pending_bookings} pending`} />
        <StatCard label="Pros" value={stats.total_pros} sub={`${stats.verified_pros} verified`} />
        <StatCard label="Audit Events Today" value={stats.total_audit_events_today} />
      </div>

      <div style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 'var(--r)', padding: '20px 24px' }}>
        <div style={{ fontWeight: 600, marginBottom: 14, color: 'var(--forest)' }}>Users by Role</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {Object.entries(stats.users_by_role).map(([role, count]) => (
            <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--paper-2)', padding: '8px 14px', borderRadius: 'var(--r-sm)' }}>
              <Badge label={role} color={roleColors[role] || 'grey'} />
              <span style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: 20, color: 'var(--ink)' }}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
function UsersTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [query, setQuery] = useState({ page: 1, search: '', role: '' });
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const { data, loading, error, reload } = useAsync(
    () => adminAPI.listUsers({ page: query.page, page_size: 25, search: query.search || undefined, role: query.role || undefined }),
    [query]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery({ page: 1, search, role: roleFilter });
    setPage(1);
  };

  const handleSave = async () => {
    if (!editUser) return;
    setSaving(true);
    setSaveError(null);
    try {
      await adminAPI.updateUser(editUser.id, {
        role: editUser.role,
        is_active: editUser.is_active,
        email_verified: editUser.email_verified,
      });
      setEditUser(null);
      reload();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Deactivate this user? They will no longer be able to log in.')) return;
    try {
      await adminAPI.deactivateUser(userId);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const roleColors = { owner: 'green', agent: 'blue', buyer: 'amber', pro: 'purple', admin: 'red' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Search bar */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          placeholder="Search name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, height: 36, padding: '0 12px', border: '1px solid var(--hairline)', borderRadius: 'var(--r-sm)', background: 'var(--card)', fontSize: 13 }}
        />
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          style={{ height: 36, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 'var(--r-sm)', background: 'var(--card)', fontSize: 13 }}
        >
          <option value="">All roles</option>
          {['owner', 'agent', 'buyer', 'pro', 'admin'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit" className="btn primary">Search</button>
      </form>

      {loading && <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error && <ErrorBanner msg={error} onRetry={reload} />}

      {data && (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            {data.total} users · page {data.page} of {Math.ceil(data.total / data.page_size)}
          </div>
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--paper-2)', borderBottom: '1px solid var(--hairline)' }}>
                  {['Name', 'Email', 'Role', 'Status', 'Joined', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--card)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }} title={`${u.first_name} ${u.last_name}`}>{maskUserName(u.first_name, u.last_name)}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 12 }} title={u.email}>{maskEmail(u.email)}</td>
                    <td style={{ padding: '10px 14px' }}><Badge label={u.role} color={roleColors[u.role] || 'grey'} /></td>
                    <td style={{ padding: '10px 14px' }}>
                      <Badge label={u.is_active ? 'Active' : 'Inactive'} color={u.is_active ? 'green' : 'red'} />
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-4)', fontSize: 12 }}>
                      {new Date(u.created_at).toLocaleDateString('en-AU')}
                    </td>
                    <td style={{ padding: '10px 14px', display: 'flex', gap: 6 }}>
                      <button className="btn sm" onClick={() => setEditUser({ ...u })}>Edit</button>
                      {u.is_active && (
                        <button className="btn sm" style={{ color: 'var(--crimson)', borderColor: 'var(--crimson)' }} onClick={() => handleDeactivate(u.id)}>
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn sm" disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); setQuery(q => ({ ...q, page: p })); }}>← Prev</button>
            <button className="btn sm" disabled={data.total <= page * data.page_size} onClick={() => { const p = page + 1; setPage(p); setQuery(q => ({ ...q, page: p })); }}>Next →</button>
          </div>
        </>
      )}

      {/* Edit modal */}
      {editUser && (
        <Modal title={`Edit User: ${maskUserName(editUser.first_name, editUser.last_name)}`} onClose={() => setEditUser(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FormField label="Role">
              <select value={editUser.role} onChange={e => setEditUser(u => ({ ...u, role: e.target.value }))} className="input">
                {['owner', 'agent', 'buyer', 'pro', 'admin'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormField>
            <FormField label="Active">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={editUser.is_active} onChange={e => setEditUser(u => ({ ...u, is_active: e.target.checked }))} />
                <span>{editUser.is_active ? 'Active' : 'Inactive'}</span>
              </label>
            </FormField>
            <FormField label="Email verified">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={editUser.email_verified} onChange={e => setEditUser(u => ({ ...u, email_verified: e.target.checked }))} />
                <span>{editUser.email_verified ? 'Verified' : 'Not verified'}</span>
              </label>
            </FormField>
            {saveError && <div style={{ color: 'var(--crimson)', fontSize: 12 }}>{saveError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Professionals Tab ────────────────────────────────────────
function ProfessionalsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tradeFilter, setTradeFilter] = useState('');
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [query, setQuery] = useState({ page: 1, search: '', trade: '', hidden: false });
  const [togglingId, setTogglingId] = useState(null);
  const [toggleError, setToggleError] = useState(null);

  const { data, loading, error, reload } = useAsync(
    () => adminAPI.listProfessionals({ page: query.page, page_size: 25, search: query.search || undefined, trade: query.trade || undefined, hidden: query.hidden }),
    [query]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery({ page: 1, search, trade: tradeFilter, hidden: hiddenOnly });
    setPage(1);
  };

  const handleToggleVisibility = async (proId, currentHidden) => {
    setTogglingId(proId);
    setToggleError(null);
    try {
      await adminAPI.toggleProfessionalVisibility(proId, !currentHidden);
      reload();
    } catch (e) {
      let msg = 'Failed to toggle visibility';
      const data = e?.response?.data;
      if (typeof data === 'string') {
        msg = data;
      } else if (data?.detail) {
        msg = data.detail;
      } else if (Array.isArray(data)) {
        msg = data[0]?.msg || msg;
      } else if (data?.msg) {
        msg = data.msg;
      } else if (typeof data === 'object') {
        msg = 'Server error: Invalid response format';
      }
      setToggleError(msg);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Search bar */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          placeholder="Search business name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, height: 36, padding: '0 12px', border: '1px solid var(--hairline)', borderRadius: 'var(--r-sm)', background: 'var(--card)', fontSize: 13 }}
        />
        <input
          placeholder="Trade (optional)…"
          value={tradeFilter}
          onChange={e => setTradeFilter(e.target.value)}
          style={{ flex: 1, minWidth: 150, height: 36, padding: '0 12px', border: '1px solid var(--hairline)', borderRadius: 'var(--r-sm)', background: 'var(--card)', fontSize: 13 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={hiddenOnly} onChange={e => setHiddenOnly(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Hidden only</span>
        </label>
        <button type="submit" className="btn primary">Search</button>
      </form>

      {toggleError && <div style={{ padding: 12, background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13 }}>{toggleError}</div>}
      {loading && <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error && <ErrorBanner msg={error} onRetry={reload} />}

      {data && (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
            {data.total} professionals · page {data.page} of {Math.ceil(data.total / data.page_size)}
          </div>
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--paper-2)', borderBottom: '1px solid var(--hairline)' }}>
                  {['Business Name', 'Trade', 'Email', 'Status', 'Rating', 'Visibility', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--card)' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{p.business_name}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-3)', fontSize: 12 }}>{p.trade}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>{p.email}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <Badge label={p.is_atlas_verified ? 'Verified' : 'Unverified'} color={p.is_atlas_verified ? 'green' : 'grey'} />
                    </td>
                    <td style={{ padding: '10px 14px', fontFamily: 'var(--serif)', fontWeight: 600 }}>
                      {p.rating > 0 ? `${p.rating.toFixed(1)}★ (${p.review_count})` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <Badge label={p.is_hidden ? 'Hidden' : 'Visible'} color={p.is_hidden ? 'red' : 'green'} />
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      <button
                        className="btn sm"
                        disabled={togglingId === p.id}
                        onClick={() => handleToggleVisibility(p.id, p.is_hidden)}
                        style={{ color: p.is_hidden ? 'var(--moss)' : 'var(--crimson)', borderColor: p.is_hidden ? 'var(--moss)' : 'var(--crimson)' }}
                      >
                        {togglingId === p.id ? '…' : (p.is_hidden ? 'Show' : 'Hide')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn sm" disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); setQuery(q => ({ ...q, page: p })); }}>← Prev</button>
            <button className="btn sm" disabled={data.total <= page * data.page_size} onClick={() => { const p = page + 1; setPage(p); setQuery(q => ({ ...q, page: p })); }}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Config Tab ────────────────────────────────────────────────────────────────
function ConfigTab() {
  const { data: items, loading, error, reload } = useAsync(() => adminAPI.listConfig());
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await adminAPI.updateConfig(editItem.key, { value: editItem.value, label: editItem.label, description: editItem.description });
      setEditItem(null);
      reload();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await adminAPI.createConfig(newItem);
      setNewItem(null);
      reload();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Delete config key "${key}"?`)) return;
    try {
      await adminAPI.deleteConfig(key);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const grouped = (items || []).reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary" onClick={() => setNewItem({ key: '', value: '', value_type: 'string', label: '', description: '', category: 'general', is_secret: false })}>
          + New Config Key
        </button>
      </div>

      {loading && <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error && <ErrorBanner msg={error} onRetry={reload} />}

      {Object.entries(grouped).map(([category, catItems]) => (
        <div key={category}>
          <div style={{ fontWeight: 600, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 10 }}>{category}</div>
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
            {catItems.map((item, i) => (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--card)', borderBottom: i < catItems.length - 1 ? '1px solid var(--hairline)' : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600, color: 'var(--forest)' }}>{item.key}</div>
                  {item.label && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 1 }}>{item.label}</div>}
                </div>
                <div style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.is_secret ? '••••••••' : (item.value || <span style={{ color: 'var(--ink-4)' }}>—</span>)}
                </div>
                <Badge label={item.value_type} color="grey" />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn sm" onClick={() => setEditItem({ ...item })}>Edit</button>
                  <button className="btn sm" style={{ color: 'var(--crimson)', borderColor: 'var(--crimson)' }} onClick={() => handleDelete(item.key)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {items && items.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--ink-4)', padding: 48 }}>
          No configuration keys yet. Add your first one above.
        </div>
      )}

      {/* Edit modal */}
      {editItem && (
        <Modal title={`Edit: ${editItem.key}`} onClose={() => setEditItem(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField label="Value">
              <input className="input" value={editItem.value} onChange={e => setEditItem(c => ({ ...c, value: e.target.value }))} />
            </FormField>
            <FormField label="Label">
              <input className="input" value={editItem.label || ''} onChange={e => setEditItem(c => ({ ...c, label: e.target.value }))} />
            </FormField>
            <FormField label="Description">
              <textarea className="input" rows={3} value={editItem.description || ''} onChange={e => setEditItem(c => ({ ...c, description: e.target.value }))} style={{ height: 'auto', padding: '8px 12px' }} />
            </FormField>
            {saveError && <div style={{ color: 'var(--crimson)', fontSize: 12 }}>{saveError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="btn primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create modal */}
      {newItem && (
        <Modal title="New Config Key" onClose={() => setNewItem(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField label="Key (lowercase, dots/underscores allowed)">
              <input className="input" placeholder="e.g. feature.max_upload_mb" value={newItem.key} onChange={e => setNewItem(c => ({ ...c, key: e.target.value }))} />
            </FormField>
            <FormField label="Value">
              <input className="input" value={newItem.value} onChange={e => setNewItem(c => ({ ...c, value: e.target.value }))} />
            </FormField>
            <FormField label="Type">
              <select className="input" value={newItem.value_type} onChange={e => setNewItem(c => ({ ...c, value_type: e.target.value }))}>
                {['string', 'number', 'boolean', 'json'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormField>
            <FormField label="Category">
              <input className="input" placeholder="general" value={newItem.category} onChange={e => setNewItem(c => ({ ...c, category: e.target.value }))} />
            </FormField>
            <FormField label="Label">
              <input className="input" value={newItem.label} onChange={e => setNewItem(c => ({ ...c, label: e.target.value }))} />
            </FormField>
            <FormField label="Secret">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={newItem.is_secret} onChange={e => setNewItem(c => ({ ...c, is_secret: e.target.checked }))} />
                <span>Mask value in UI</span>
              </label>
            </FormField>
            {saveError && <div style={{ color: 'var(--crimson)', fontSize: 12 }}>{saveError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setNewItem(null)}>Cancel</button>
              <button className="btn primary" disabled={saving || !newItem.key} onClick={handleCreate}>{saving ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Pricing Tab ───────────────────────────────────────────────────────────────
function PricingTab() {
  const { data: plans, loading, error, reload } = useAsync(() => adminAPI.listPlans());
  const [editPlan, setEditPlan] = useState(null);
  const [newPlan, setNewPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { id, created_at, slug, role, ...patch } = editPlan;
      await adminAPI.updatePlan(id, patch);
      setEditPlan(null);
      reload();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await adminAPI.createPlan(newPlan);
      setNewPlan(null);
      reload();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"?`)) return;
    try {
      await adminAPI.deletePlan(id);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const roleColors = { owner: 'green', agent: 'blue', buyer: 'amber', pro: 'purple' };
  const emptyPlan = { name: '', slug: '', description: '', role: 'owner', price_monthly: 0, price_yearly: null, currency: 'AUD', max_properties: null, features: [], is_active: true, is_popular: false, sort_order: 0, stripe_price_id_monthly: '', stripe_price_id_yearly: '', stripe_product_id: '' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary" onClick={() => setNewPlan({ ...emptyPlan })}>+ New Plan</button>
      </div>

      {loading && <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error && <ErrorBanner msg={error} onRetry={reload} />}

      {plans && plans.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--ink-4)', padding: 48 }}>
          No pricing plans yet. Create your first plan above.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {(plans || []).map(plan => (
          <div key={plan.id} style={{ background: 'var(--card)', border: plan.is_popular ? '2px solid var(--forest)' : '1px solid var(--hairline)', borderRadius: 'var(--r)', padding: '20px', position: 'relative' }}>
            {plan.is_popular && (
              <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: 'var(--forest)', color: 'var(--paper)', fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: 20 }}>Popular</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: 18, color: 'var(--ink)' }}>{plan.name}</div>
                <Badge label={plan.role} color={roleColors[plan.role] || 'grey'} />
              </div>
              <Badge label={plan.is_active ? 'Active' : 'Draft'} color={plan.is_active ? 'green' : 'grey'} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontFamily: 'var(--serif)', fontSize: 28, fontWeight: 700, color: 'var(--forest)' }}>
                {plan.price_monthly === 0 ? 'Free' : `$${plan.price_monthly}`}
              </span>
              {plan.price_monthly > 0 && <span style={{ fontSize: 12, color: 'var(--ink-4)' }}> /mo {plan.currency}</span>}
              {plan.price_yearly && <div style={{ fontSize: 12, color: 'var(--moss)' }}>${plan.price_yearly}/yr · save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%</div>}
            </div>
            {plan.description && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>{plan.description}</div>}
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4 }}>
              {plan.max_properties ? `Up to ${plan.max_properties} properties` : 'Unlimited properties'}
            </div>
            {plan.features.length > 0 && (
              <ul style={{ margin: '8px 0 0', padding: '0 0 0 16px', fontSize: 11, color: 'var(--ink-3)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {plan.features.slice(0, 4).map(f => <li key={f}>{f}</li>)}
                {plan.features.length > 4 && <li style={{ color: 'var(--ink-4)' }}>+{plan.features.length - 4} more</li>}
              </ul>
            )}
            {plan.stripe_product_id && (
              <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-4)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Stripe: {plan.stripe_product_id}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn sm" style={{ flex: 1 }} onClick={() => setEditPlan({ ...plan })}>Edit</button>
              <button className="btn sm" style={{ color: 'var(--crimson)', borderColor: 'var(--crimson)' }} onClick={() => handleDelete(plan.id, plan.name)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit plan modal */}
      {editPlan && (
        <Modal title={`Edit Plan: ${editPlan.name}`} onClose={() => setEditPlan(null)}>
          <PlanForm plan={editPlan} onChange={setEditPlan} saveError={saveError} saving={saving} onSave={handleSave} onCancel={() => setEditPlan(null)} />
        </Modal>
      )}

      {/* Create plan modal */}
      {newPlan && (
        <Modal title="New Pricing Plan" onClose={() => setNewPlan(null)}>
          <PlanForm plan={newPlan} onChange={setNewPlan} saveError={saveError} saving={saving} onSave={handleCreate} onCancel={() => setNewPlan(null)} isNew />
        </Modal>
      )}
    </div>
  );
}

function PlanForm({ plan, onChange, saveError, saving, onSave, onCancel, isNew }) {
  const [featuresText, setFeaturesText] = useState((plan.features || []).join('\n'));

  const handleFeaturesBlur = () => {
    onChange(p => ({ ...p, features: featuresText.split('\n').map(s => s.trim()).filter(Boolean) }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {isNew && (
        <>
          <FormField label="Slug (kebab-case)">
            <input className="input" placeholder="e.g. owner-pro" value={plan.slug} onChange={e => onChange(p => ({ ...p, slug: e.target.value }))} />
          </FormField>
          <FormField label="For role">
            <select className="input" value={plan.role} onChange={e => onChange(p => ({ ...p, role: e.target.value }))}>
              {['owner', 'agent', 'buyer', 'pro'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </FormField>
        </>
      )}
      <FormField label="Plan name">
        <input className="input" value={plan.name} onChange={e => onChange(p => ({ ...p, name: e.target.value }))} />
      </FormField>
      <FormField label="Description">
        <textarea className="input" rows={2} value={plan.description || ''} onChange={e => onChange(p => ({ ...p, description: e.target.value }))} style={{ height: 'auto', padding: '8px 12px' }} />
      </FormField>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Monthly price (AUD)">
          <input className="input" type="number" min="0" step="0.01" value={plan.price_monthly} onChange={e => onChange(p => ({ ...p, price_monthly: parseFloat(e.target.value) || 0 }))} />
        </FormField>
        <FormField label="Yearly price (AUD, optional)">
          <input className="input" type="number" min="0" step="0.01" value={plan.price_yearly || ''} onChange={e => onChange(p => ({ ...p, price_yearly: e.target.value ? parseFloat(e.target.value) : null }))} />
        </FormField>
      </div>
      <FormField label="Max properties (blank = unlimited)">
        <input className="input" type="number" min="1" value={plan.max_properties || ''} onChange={e => onChange(p => ({ ...p, max_properties: e.target.value ? parseInt(e.target.value) : null }))} />
      </FormField>
      <FormField label="Features (one per line)">
        <textarea
          className="input" rows={4} value={featuresText}
          onChange={e => setFeaturesText(e.target.value)}
          onBlur={handleFeaturesBlur}
          placeholder="e.g. Unlimited timeline entries&#10;Document uploads&#10;AI chat assistant"
          style={{ height: 'auto', padding: '8px 12px' }}
        />
      </FormField>
      <FormField label="Stripe Product ID">
        <input className="input" placeholder="prod_…" value={plan.stripe_product_id || ''} onChange={e => onChange(p => ({ ...p, stripe_product_id: e.target.value }))} />
      </FormField>
      <FormField label="Stripe Monthly Price ID">
        <input className="input" placeholder="price_…" value={plan.stripe_price_id_monthly || ''} onChange={e => onChange(p => ({ ...p, stripe_price_id_monthly: e.target.value }))} />
      </FormField>
      <FormField label="Stripe Yearly Price ID">
        <input className="input" placeholder="price_…" value={plan.stripe_price_id_yearly || ''} onChange={e => onChange(p => ({ ...p, stripe_price_id_yearly: e.target.value }))} />
      </FormField>
      <div style={{ display: 'flex', gap: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={plan.is_active} onChange={e => onChange(p => ({ ...p, is_active: e.target.checked }))} />
          Active
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={plan.is_popular} onChange={e => onChange(p => ({ ...p, is_popular: e.target.checked }))} />
          Mark as Popular
        </label>
      </div>
      {saveError && <div style={{ color: 'var(--crimson)', fontSize: 12 }}>{saveError}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn primary" disabled={saving || !plan.name} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

// ── Audit Tab ─────────────────────────────────────────────────────────────────
function AuditTab() {
  const [query, setQuery] = useState({ page: 1 });
  const [actionFilter, setActionFilter] = useState('');
  const [purging, setPurging] = useState(false);
  const { data, loading, error, reload } = useAsync(
    () => adminAPI.listAudit({ page: query.page, page_size: 50, action: query.action }),
    [query]
  );

  const handleFilter = (e) => {
    e.preventDefault();
    setQuery({ page: 1, action: actionFilter || undefined });
  };

  const handlePurge = async () => {
    if (!window.confirm('Delete all audit log records older than 365 days? This cannot be undone.')) return;
    setPurging(true);
    try {
      const res = await adminAPI.purgeAudit(365);
      alert(`Purged ${res.data.deleted} record(s) older than ${new Date(res.data.cutoff).toLocaleDateString('en-AU')}.`);
      reload();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <form onSubmit={handleFilter} style={{ display: 'flex', gap: 10, flex: 1 }}>
          <input
            placeholder="Filter by action (e.g. user.login)"
            value={actionFilter}
            onChange={e => setActionFilter(e.target.value)}
            style={{ flex: 1, minWidth: 180, height: 36, padding: '0 12px', border: '1px solid var(--hairline)', borderRadius: 'var(--r-sm)', background: 'var(--card)', fontSize: 13 }}
          />
          <button type="submit" className="btn">Filter</button>
          <button type="button" className="btn" onClick={() => { setActionFilter(''); setQuery({ page: 1 }); }}>Clear</button>
        </form>
        <button
          type="button"
          className="btn sm"
          style={{ color: 'var(--crimson)', borderColor: 'var(--crimson)', flexShrink: 0 }}
          onClick={handlePurge}
          disabled={purging}
          title="GDPR Art 5(1)(e) — delete records older than 365 days"
        >
          {purging ? 'Purging…' : 'Purge old records (365d+)'}
        </button>
      </div>

      {loading && <div style={{ color: 'var(--ink-4)', textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error && <ErrorBanner msg={error} onRetry={reload} />}

      {data && (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{data.total} events</div>
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--paper-2)', borderBottom: '1px solid var(--hairline)' }}>
                  {['Time', 'Action', 'Resource', 'User', 'IP'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--card)' }}>
                    <td style={{ padding: '9px 14px', fontFamily: 'var(--mono)', color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ padding: '9px 14px', fontFamily: 'var(--mono)', color: 'var(--forest)', fontWeight: 500 }}>{log.action}</td>
                    <td style={{ padding: '9px 14px', color: 'var(--ink-3)' }}>{log.resource_type}</td>
                    <td style={{ padding: '9px 14px', color: 'var(--ink-3)' }}>{log.user_email || <span style={{ color: 'var(--ink-4)' }}>—</span>}</td>
                    <td style={{ padding: '9px 14px', fontFamily: 'var(--mono)', color: 'var(--ink-4)', fontSize: 11 }}>{log.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn sm" disabled={query.page <= 1} onClick={() => setQuery(q => ({ ...q, page: q.page - 1 }))}>← Prev</button>
            <button className="btn sm" disabled={data.total <= query.page * 50} onClick={() => setQuery(q => ({ ...q, page: q.page + 1 }))}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Reports Tab ───────────────────────────────────────────────────────────────
const PERIOD_OPTIONS = [
  { value: 'day',     label: 'Daily (30d)' },
  { value: 'week',    label: 'Weekly (12w)' },
  { value: 'month',   label: 'Monthly (12m)' },
  { value: 'quarter', label: 'Quarterly (2y)' },
];

const STATE_COLORS = ['#2d6a4f','#52b788','#74c69d','#b7e4c7','#1b4332','#40916c','#95d5b2','#d8f3dc'];
const STATUS_COLORS = { active: '#2d6a4f', on_hold: '#e9c46a', completed: '#52b788', archived: '#adb5bd' };

function fmtDateLabel(iso, period) {
  if (!iso) return '';
  const d = new Date(iso);
  if (period === 'day')     return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
  if (period === 'week')    return `W${getISOWeek(d)} '${String(d.getFullYear()).slice(2)}`;
  if (period === 'month')   return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
  if (period === 'quarter') {
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `Q${q} '${String(d.getFullYear()).slice(2)}`;
  }
  return iso.slice(0, 10);
}

function getISOWeek(d) {
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
  return Math.ceil(((d - startOfWeek1) / 86400000 + 1) / 7);
}

function EmptyChart({ height }) {
  return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)', fontSize: 13, background: 'var(--paper-2)', borderRadius: 'var(--r-sm)' }}>
      No data for this period
    </div>
  );
}

function LineChart({ data, period, color = '#2d6a4f', height = 180 }) {
  if (!data || data.length === 0) return <EmptyChart height={height} />;
  const W = 560; const H = height;
  const pad = { t: 16, r: 16, b: 36, l: 44 };
  const cW = W - pad.l - pad.r;
  const cH = H - pad.t - pad.b;
  const vals = data.map(d => d.count);
  const maxV = Math.max(...vals, 1);
  const step = Math.max(1, Math.ceil(data.length / 7));

  const px = (i) => pad.l + (data.length === 1 ? cW / 2 : (i / (data.length - 1)) * cW);
  const py = (v) => pad.t + cH - (v / maxV) * cH;

  const pts = data.map((d, i) => `${px(i)},${py(d.count)}`).join(' ');
  const area = `${px(0)},${pad.t + cH} ${pts} ${px(data.length - 1)},${pad.t + cH}`;

  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
      {yTicks.map(f => {
        const yp = pad.t + cH * (1 - f);
        return (
          <g key={f}>
            <line x1={pad.l} y1={yp} x2={W - pad.r} y2={yp} stroke="#e8ede9" strokeDasharray="4,3" />
            <text x={pad.l - 6} y={yp + 4} textAnchor="end" fill="#9aad9e" fontSize={9}>{Math.round(maxV * f)}</text>
          </g>
        );
      })}
      <polygon points={area} fill={color} fillOpacity={0.1} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => (
        <circle key={i} cx={px(i)} cy={py(d.count)} r={data.length > 20 ? 2 : 3} fill={color} />
      ))}
      {data.map((d, i) => i % step === 0 && (
        <text key={i} x={px(i)} y={H - 6} textAnchor="middle" fill="#9aad9e" fontSize={9}>
          {fmtDateLabel(d.date, period)}
        </text>
      ))}
    </svg>
  );
}

function StackedBarChart({ data, keys, colors, period, height = 200 }) {
  if (!data || data.length === 0) return <EmptyChart height={height} />;
  const W = 560; const H = height;
  const pad = { t: 16, r: 16, b: 36, l: 44 };
  const cW = W - pad.l - pad.r;
  const cH = H - pad.t - pad.b;
  const totals = data.map(d => keys.reduce((s, k) => s + (d[k] || 0), 0));
  const maxV = Math.max(...totals, 1);
  const bW = Math.max(6, Math.min(36, (cW / (data.length || 1)) * 0.65));
  const step = Math.max(1, Math.ceil(data.length / 7));

  const bx = (i) => pad.l + (i + 0.5) * (cW / (data.length || 1)) - bW / 2;
  const scaleH = (v) => (v / maxV) * cH;

  const yTicks = [0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
      {yTicks.map(f => {
        const yp = pad.t + cH * (1 - f);
        return (
          <g key={f}>
            <line x1={pad.l} y1={yp} x2={W - pad.r} y2={yp} stroke="#e8ede9" strokeDasharray="4,3" />
            <text x={pad.l - 6} y={yp + 4} textAnchor="end" fill="#9aad9e" fontSize={9}>{Math.round(maxV * f)}</text>
          </g>
        );
      })}
      {data.map((d, i) => {
        let acc = 0;
        return (
          <g key={i}>
            {keys.map((k, ki) => {
              const val = d[k] || 0;
              const bH = scaleH(val);
              const by = pad.t + cH - scaleH(acc + val);
              acc += val;
              return (
                <rect key={k} x={bx(i)} y={by} width={bW} height={bH} fill={colors[ki] || '#ccc'} rx={2} />
              );
            })}
            {i % step === 0 && (
              <text x={bx(i) + bW / 2} y={H - 6} textAnchor="middle" fill="#9aad9e" fontSize={9}>
                {fmtDateLabel(d.date, period)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function ChartCard({ title, children, legend }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 'var(--r)', padding: '20px 24px' }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--forest)', marginBottom: 4 }}>{title}</div>
      {legend && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          {legend.map(({ label, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-3)' }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color }} />
              {label}
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

function ReportsTab() {
  const [period, setPeriod] = useState('month');
  const { data, loading, error, reload } = useAsync(
    () => adminAPI.getReports(period),
    [period]
  );

  const fmtAUD = (v) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(v || 0);

  const stateLegend = data
    ? (data.properties_by_state.states || []).map((s, i) => ({ label: s, color: STATE_COLORS[i % STATE_COLORS.length] }))
    : [];

  const statusLegend = (data?.projects_by_status?.statuses || []).map(s => ({
    label: s.replace('_', ' '),
    color: STATUS_COLORS[s] || '#888',
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Period selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--ink-4)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Period</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`btn sm${period === opt.value ? ' primary' : ''}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={{ color: 'var(--ink-4)', padding: 40, textAlign: 'center' }}>Loading reports…</div>}
      {error && <ErrorBanner msg={error} onRetry={reload} />}

      {data && (
        <>
          {/* Summary tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
            <StatCard label="Total Jobs" value={data.jobs.total} sub="All time" />
            <StatCard label="EOI Requests" value={data.eoi.total} sub={`${data.eoi.responded_pct}% responded`} />
            <StatCard
              label="EOI Responded"
              value={`${data.eoi.responded_pct}%`}
              sub={`${data.eoi.responded} of ${data.eoi.total}`}
              accent={data.eoi.responded_pct >= 50 ? 'var(--moss)' : 'var(--terracotta)'}
            />
            <StatCard
              label="Portfolio Value"
              value={fmtAUD(data.portfolio_value)}
              sub="COALESCE(market, purchase)"
            />
          </div>

          {/* Login timeline */}
          <ChartCard title="User Logins">
            <LineChart data={data.logins} period={period} color="#2d6a4f" height={180} />
          </ChartCard>

          {/* Properties per state */}
          <ChartCard
            title="Properties Added per State"
            legend={stateLegend}
          >
            {data.properties_by_state.states.length > 0 ? (
              <StackedBarChart
                data={data.properties_by_state.series}
                keys={data.properties_by_state.states}
                colors={data.properties_by_state.states.map((_, i) => STATE_COLORS[i % STATE_COLORS.length])}
                period={period}
                height={200}
              />
            ) : (
              <EmptyChart height={200} />
            )}
          </ChartCard>

          {/* Projects by status */}
          <ChartCard
            title="Projects by Status"
            legend={statusLegend}
          >
            <StackedBarChart
              data={data.projects_by_status.series}
              keys={data.projects_by_status.statuses}
              colors={data.projects_by_status.statuses.map(s => STATUS_COLORS[s] || '#888')}
              period={period}
              height={200}
            />
          </ChartCard>

          {/* Jobs timeline */}
          <ChartCard title="Jobs Requested">
            <LineChart data={data.jobs.timeline} period={period} color="#52b788" height={180} />
          </ChartCard>
        </>
      )}
    </div>
  );
}

// ── Shared UI helpers ─────────────────────────────────────────────────────────
function ErrorBanner({ msg, onRetry }) {
  let errorMsg = 'An error occurred';
  if (typeof msg === 'string') {
    errorMsg = msg;
  } else if (msg?.detail) {
    errorMsg = msg.detail;
  } else if (msg?.msg) {
    errorMsg = msg.msg;
  } else if (Array.isArray(msg)) {
    errorMsg = msg[0]?.msg || msg[0]?.detail || 'An error occurred';
  }
  return (
    <div style={{ background: '#fdecea', border: '1px solid #f5c6c6', borderRadius: 'var(--r)', padding: '12px 16px', color: '#b71c1c', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{errorMsg}</span>
      {onRetry && <button className="btn sm" onClick={onRetry} style={{ borderColor: '#f5c6c6', color: '#b71c1c' }}>Retry</button>}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--card)', borderRadius: 'var(--r-lg)', padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: 18, color: 'var(--forest)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--ink-4)', lineHeight: 1 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'reports', label: 'Reports' },
  { id: 'users', label: 'Users' },
  { id: 'professionals', label: 'Professionals' },
  { id: 'config', label: 'Configuration' },
  { id: 'pricing', label: 'Pricing Plans' },
  { id: 'audit', label: 'Audit Log' },
];

export default function AdminDashboardPage() {
  const [tab, setTab] = useState('overview');

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1200 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700, color: 'var(--forest)', marginBottom: 4 }}>Admin Dashboard</div>
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Platform management · PropAtlas</div>
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview' && <OverviewTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'professionals' && <ProfessionalsTab />}
      {tab === 'config' && <ConfigTab />}
      {tab === 'pricing' && <PricingTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}
